<script setup lang="ts">
const props = defineProps<{
  modelValue: boolean;
  eventId: number;
}>();
const emit = defineEmits(['update:modelValue']);

const { data } = useAuth();
const user = computed(() => data.value?.data ?? null);
const badgeStore = useBadgeStore();

const open = computed({
  get: () => props.modelValue,
  set: (v: boolean) => emit('update:modelValue', v),
});

const qrUrl = ref<string>('');
const showScanner = ref<boolean>(false);

// Lazily fetch the badge QR (tito) the first time the modal opens; reset the scanner on close.
watch(() => props.modelValue, async (isOpen) => {
  if (!isOpen) {
    showScanner.value = false;
    return;
  }
  if (qrUrl.value || !props.eventId) {
    return;
  }
  try {
    // Goes through the badge store so the QR comes from (and is saved to) the same
    // user-scoped offline cache as the My Badge page. Falls back to the cached badge when
    // offline; only throws when nothing is cached, in which case the QR stays empty.
    const details = await badgeStore.loadContactDetails(props.eventId);
    qrUrl.value = details?.tito_qr_code_url || '';
  } catch {}
});
</script>

<template>
  <client-only>
    <common-wad-modal v-model="open" :show-close-icon="true">
      <template #content>
        <div v-if="!showScanner" class="wad4-badge-modal">
          <p class="wad4-badge-modal__hint">
            Share your profile by letting other attendees scan your badge, or scan theirs to add them to your contacts.
          </p>
          <event-ticket-card
              v-if="user"
              :name="user.full_name"
              :email="user.email"
              :qr-url="qrUrl"
              :image-url="user.image_url"
          />
        </div>
        <div v-else class="wad4-badge-modal__scanner">
          <common-qr-reader :lead-scan="false" />
        </div>
      </template>
      <template #controls>
        <button v-if="!showScanner" type="button" class="wad4-button-primary wad4-button--width-full" @click="showScanner = true">
          <span class="wad4-button__text">Scan to add a contact</span>
        </button>
      </template>
    </common-wad-modal>
  </client-only>
</template>

<style lang="scss">
.wad4-badge-modal {
  display: flex;
  flex-direction: column;
  gap: $wad4-spacing-3xl;
  width: 100%;

  &__hint {
    margin: 0;
    text-align: center;
    color: $wad4-c-text-text-secondary;
    @include text-size-mixin('sm');
  }

  &__scanner {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: $wad4-spacing-lg;
    width: 100%;
  }
}
</style>
